import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../lib/api.js';

// Response shapes for the commission-earnings endpoints (Brief
// BUILD-COMMISSION-VIEW). Declared here (web-side) because the API keeps these
// DTOs feature-local; they mirror
// apps/api/src/features/commission-earnings/earnings.schemas.ts exactly.

export type EarningsScope = 'all_branches' | 'own_branch';
export type EarningsPaymentRoute = 'via_aussizz' | 'direct' | 'unspecified';

export type CommissionDeal = {
  quoteId: string;
  quoteNumber: string;
  quoteDate: string;
  clientName: string;
  branchId: string | null;
  branchName: string | null;
  collegeName: string;
  courseName: string;
  kind: 'terms' | 'none' | 'not_set';
  rateBp: number | null;
  flatAmountCents: number | null;
  basis: string;
  basisMonths: number | null;
  deduction: string | null;
  paymentRoute: EarningsPaymentRoute;
  resolvedScope: 'course' | 'provider' | null;
  netFeeCents: number;
  indicativeCommissionCents: number;
};

export type CollegeBreakdown = {
  collegeName: string;
  totalIndicativeCents: number;
  dealCount: number;
};

export type BranchBreakdown = {
  branchId: string | null;
  branchName: string;
  totalIndicativeCents: number;
  dealCount: number;
};

export type RouteBreakdown = {
  paymentRoute: EarningsPaymentRoute;
  totalIndicativeCents: number;
  dealCount: number;
};

export type CommissionEarningsResponse = {
  indicative: true;
  disclaimer: string;
  scope: EarningsScope;
  totalIndicativeCents: number;
  dealCount: number;
  byCollege: CollegeBreakdown[];
  byBranch: BranchBreakdown[];
  byPaymentRoute: RouteBreakdown[];
};

export type CommissionDealsResponse = {
  indicative: true;
  disclaimer: string;
  scope: EarningsScope;
  deals: CommissionDeal[];
};

const earningsKey = ['commission', 'earnings'] as const;
const dealsKey = [...earningsKey, 'deals'] as const;

/** Totals + per-college / per-branch / per-route breakdowns (read-only). */
export function useCommissionEarnings() {
  return useQuery({
    queryKey: earningsKey,
    queryFn: () => apiGet<CommissionEarningsResponse>('/commission/earnings'),
    staleTime: 30_000,
  });
}

/** The per-deal list (read-only). */
export function useCommissionDeals() {
  return useQuery({
    queryKey: dealsKey,
    queryFn: () => apiGet<CommissionDealsResponse>('/commission/earnings/deals'),
    staleTime: 30_000,
  });
}
